import { useMemo, useRef, useState } from "react";

type Props = {
  mode: "set" | "unlock";
  onComplete: (dots: number[]) => void;
  onCancel?: () => void;
};

const SIZE = 3;
const DOTS = SIZE * SIZE;

/**
 * Simple Android-style pattern lock MVP (3×3).
 */
export function PatternLock({ mode, onComplete, onCancel }: Props) {
  const [path, setPath] = useState<number[]>([]);
  const drawing = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const title =
    mode === "set" ? "الگوی باز کردن را بکشید" : "الگوی امضا را وارد کنید";

  const coords = useMemo(() => {
    const out: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < DOTS; i++) {
      const col = i % SIZE;
      const row = Math.floor(i / SIZE);
      out.push({ x: 20 + col * 30, y: 20 + row * 30 });
    }
    return out;
  }, []);

  const hit = (clientX: number, clientY: number): number | null => {
    const el = boxRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    for (let i = 0; i < DOTS; i++) {
      const c = coords[i]!;
      const dx = x - c.x;
      const dy = y - c.y;
      if (dx * dx + dy * dy <= 10 * 10) return i;
    }
    return null;
  };

  const finish = (next: number[]) => {
    if (next.length >= 4) onComplete(next);
    setPath([]);
  };

  return (
    <div className="esign-pattern">
      <p className="signing-muted">{title}</p>
      <div
        ref={boxRef}
        className="esign-pattern-box"
        onPointerDown={(e) => {
          drawing.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          const i = hit(e.clientX, e.clientY);
          setPath(i != null ? [i] : []);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const i = hit(e.clientX, e.clientY);
          if (i == null) return;
          setPath((prev) => (prev.includes(i) ? prev : [...prev, i]));
        }}
        onPointerUp={() => {
          drawing.current = false;
          finish(path);
        }}
      >
        <svg viewBox="0 0 100 100" className="esign-pattern-svg" aria-hidden>
          {path.length > 1 ? (
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              points={path
                .map((i) => `${coords[i]!.x},${coords[i]!.y}`)
                .join(" ")}
            />
          ) : null}
          {coords.map((c, i) => (
            <circle
              key={i}
              cx={c.x}
              cy={c.y}
              r={path.includes(i) ? 5.5 : 4}
              className={path.includes(i) ? "active" : undefined}
            />
          ))}
        </svg>
      </div>
      {onCancel ? (
        <button type="button" className="ghost-btn" onClick={onCancel}>
          انصراف
        </button>
      ) : null}
    </div>
  );
}
